import variant from '@jitl/quickjs-singlefile-browser-release-sync';
import {
  newQuickJSWASMModuleFromVariant,
  type QuickJSContext,
  type QuickJSHandle,
  type QuickJSRuntime,
  type QuickJSWASMModule,
} from 'quickjs-emscripten-core';

import { proxyFetch as defaultProxyFetch, type ProxyFetchOpts } from '../../lib/proxy.ts';
import type { SourceAuth, SourceId } from '../types.ts';
import {
  decodeBase64,
  decryptAesEcb,
  encodeBase64,
  hexEncode,
  hmacString,
  md5,
  sha256Hex,
} from './convert.ts';
import {
  UnsupportedVeneraApiError,
  VENERA_HOST_ERROR_PREFIX,
  errorMessage,
} from './errors.ts';
import type { VeneraPluginStorage, VeneraRuntime, VeneraRuntimePlugin } from './types.ts';

interface BridgeOptions {
  sourceId: SourceId;
  jsSource: string;
  settings?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
  storage: VeneraPluginStorage;
  proxyFetch?: (url: string, opts?: ProxyFetchOpts) => Promise<Response>;
}

interface HostState {
  auth: SourceAuth | undefined;
  deadline: number;
  activeHostCalls: number;
}

interface BridgeContext {
  readonly vm: QuickJSContext;
  readonly runtime: QuickJSRuntime;
  readonly hostState: HostState;
  readonly timers: Map<number, QuickJSHandle>;
  toHandle(value: unknown): QuickJSHandle;
  fromHandle(handle: QuickJSHandle): unknown;
  callAsync(path: readonly string[], args: unknown[], auth?: SourceAuth): Promise<unknown>;
  dispose(): void;
}

const DEFAULT_TIMEOUT_MS = 12_000;
const DEFAULT_MEMORY_LIMIT_BYTES = 32 * 1024 * 1024;
const DEFAULT_STACK_SIZE_BYTES = 1024 * 1024;
const MAX_HOST_CALLS = 16;
const FORWARDED_HEADERS_HEADER = 'X-Forward-Headers';

let quickJsPromise: Promise<QuickJSWASMModule> | undefined;

export function loadQuickJs(): Promise<QuickJSWASMModule> {
  quickJsPromise ??= newQuickJSWASMModuleFromVariant(variant);
  return quickJsPromise;
}

export async function createVeneraRuntime(options: BridgeOptions): Promise<VeneraRuntime> {
  const quickJs = await loadQuickJs();
  const hostState: HostState = {
    auth: undefined,
    deadline: Number.POSITIVE_INFINITY,
    activeHostCalls: 0,
  };
  const runtime = quickJs.newRuntime({
    memoryLimitBytes: DEFAULT_MEMORY_LIMIT_BYTES,
    maxStackSizeBytes: DEFAULT_STACK_SIZE_BYTES,
    interruptHandler: () => Date.now() > hostState.deadline,
  });
  const vm = runtime.newContext();
  const bridge = createBridgeContext(vm, runtime, hostState);
  installHostApis(bridge, options);

  hostState.deadline = Date.now() + DEFAULT_TIMEOUT_MS;
  const evalResult = vm.evalCode(options.jsSource, 'venera-plugin.js');
  hostState.deadline = Number.POSITIVE_INFINITY;
  const pluginHandle = vm.unwrapResult(evalResult);
  vm.setProp(vm.global, '__veneraPlugin', pluginHandle);
  const plugin = normalizePlugin(bridge.fromHandle(pluginHandle));
  pluginHandle.dispose();

  return {
    plugin,
    async call(path, args, auth) {
      return bridge.callAsync(path, args, auth);
    },
    dispose() {
      bridge.dispose();
      vm.dispose();
      runtime.dispose();
    },
  };
}

function createBridgeContext(
  vm: QuickJSContext,
  runtime: QuickJSRuntime,
  hostState: HostState,
): BridgeContext {
  const timers = new Map<number, QuickJSHandle>();
  let callQueue: Promise<void> = Promise.resolve();
  return {
    vm,
    runtime,
    hostState,
    timers,
    toHandle(value: unknown): QuickJSHandle {
      if (value === undefined) return vm.undefined;
      return vm.unwrapResult(vm.evalCode(`(${JSON.stringify(value)})`));
    },
    fromHandle(handle: QuickJSHandle): unknown {
      return vm.dump(handle);
    },
    async callAsync(path, args, auth) {
      const previousCall = callQueue;
      let releaseQueue: () => void = () => {};
      callQueue = new Promise<void>((resolve) => {
        releaseQueue = resolve;
      });
      await previousCall;
      hostState.auth = auth;
      hostState.deadline = Date.now() + DEFAULT_TIMEOUT_MS;
      try {
        let target = vm.global;
        const owned: QuickJSHandle[] = [];
        for (const segment of path) {
          const next = vm.getProp(target, segment);
          if (target !== vm.global) owned.push(target);
          target = next;
        }
        initializePluginForCall(this);
        const argHandles = args.map((arg) => this.toHandle(arg));
        const result = vm.unwrapResult(vm.callFunction(target, vm.undefined, argHandles));
        for (const handle of argHandles) handle.dispose();
        for (const handle of owned) handle.dispose();
        target.dispose();
        const resolved = await withTimeout(
          resolveMaybePromise(vm, runtime, result),
          DEFAULT_TIMEOUT_MS,
          'Venera plugin call timed out',
        );
        const normalized = normalizeQuickJsValue(this, resolved);
        const value = this.fromHandle(normalized);
        normalized.dispose();
        resolved.dispose();
        return value;
      } finally {
        hostState.auth = undefined;
        hostState.deadline = Number.POSITIVE_INFINITY;
        releaseQueue();
      }
    },
    dispose() {
      for (const [timerId, callback] of timers) {
        globalThis.clearTimeout(timerId);
        callback.dispose();
      }
      timers.clear();
    },
  };
}

function initializePluginForCall(bridge: BridgeContext): void {
  const result = bridge.vm.evalCode(`
    if (
      globalThis.__veneraPlugin &&
      typeof globalThis.__veneraPlugin.init === 'function'
    ) {
      globalThis.__veneraPlugin.init();
    }
    null;
  `);
  bridge.vm.unwrapResult(result).dispose();
}

function normalizeQuickJsValue(bridge: BridgeContext, value: QuickJSHandle): QuickJSHandle {
  const normalizer = bridge.vm.getProp(bridge.vm.global, '__bunkoNormalize');
  const result = bridge.vm.unwrapResult(bridge.vm.callFunction(normalizer, bridge.vm.undefined, [value]));
  normalizer.dispose();
  return result;
}

async function resolveMaybePromise(
  vm: QuickJSContext,
  runtime: QuickJSRuntime,
  handle: QuickJSHandle,
): Promise<QuickJSHandle> {
  const promiseState = vm.getPromiseState(handle);
  if (promiseState.type === 'fulfilled' && promiseState.notAPromise) return handle;

  const promise = vm.resolvePromise(handle);
  handle.dispose();
  let settled = false;
  let settledResult: Awaited<typeof promise> | undefined;
  let settledError: unknown;
  void promise
    .then((result) => {
      settled = true;
      settledResult = result;
    })
    .catch((err: unknown) => {
      settled = true;
      settledError = err;
    });
  while (!settled) {
    while (runtime.hasPendingJob()) {
      const jobs = runtime.executePendingJobs();
      if (jobs.error) {
        const error = vm.dump(jobs.error);
        jobs.dispose();
        throw new Error(errorMessage(error));
      }
      jobs.dispose();
    }
    await new Promise((resolve) => globalThis.setTimeout(resolve, 0));
  }
  if (settledError !== undefined) throw settledError;
  if (settledResult === undefined) throw new Error('QuickJS promise resolved without a result.');
  const resolved = settledResult;
  if (resolved.error) {
    const error = vm.dump(resolved.error);
    resolved.error.dispose();
    throw new Error(errorMessage(error));
  }
  return resolved.value;
}

function setGlobal(bridge: BridgeContext, name: string, value: QuickJSHandle): void {
  bridge.vm.setProp(bridge.vm.global, name, value);
  value.dispose();
}

function setObjectProp(bridge: BridgeContext, object: QuickJSHandle, name: string, value: QuickJSHandle): void {
  bridge.vm.setProp(object, name, value);
  value.dispose();
}

function hostFunction(
  bridge: BridgeContext,
  name: string,
  fn: (...args: unknown[]) => unknown | Promise<unknown>,
): QuickJSHandle {
  return bridge.vm.newFunction(name, (...handles) => {
    const args = handles.map((handle) => bridge.fromHandle(handle));
    const deferred = bridge.vm.newPromise(
      Promise.resolve()
        .then(() => withHostCallLimit(bridge, name, () => fn(...args)))
        .then((value) => bridge.toHandle(value))
        .catch((err: unknown) => {
          throw encodeHostError(err);
        }),
    );
    return deferred.handle;
  });
}

async function withHostCallLimit<T>(
  bridge: BridgeContext,
  name: string,
  fn: () => T | Promise<T>,
): Promise<T> {
  if (bridge.hostState.activeHostCalls >= MAX_HOST_CALLS) {
    throw new Error(`Venera host call limit exceeded while calling ${name}`);
  }
  bridge.hostState.activeHostCalls += 1;
  try {
    return await withTimeout(
      Promise.resolve().then(fn),
      DEFAULT_TIMEOUT_MS,
      `Venera host call timed out while calling ${name}`,
    );
  } finally {
    bridge.hostState.activeHostCalls -= 1;
  }
}

async function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = globalThis.setTimeout(() => reject(new Error(message)), ms);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
}

function syncHostFunction(
  bridge: BridgeContext,
  name: string,
  fn: (...args: unknown[]) => unknown,
): QuickJSHandle {
  return bridge.vm.newFunction(name, (...handles) => {
    const args = handles.map((handle) => bridge.fromHandle(handle));
    try {
      return bridge.toHandle(fn(...args));
    } catch (err) {
      throw encodeHostError(err);
    }
  });
}

function encodeHostError(err: unknown): Error {
  const payload: Record<string, unknown> = {
    name: err instanceof Error ? err.name : 'Error',
    message: errorMessage(err),
  };
  const maybeStatus = err && typeof err === 'object' ? (err as { status?: unknown }).status : undefined;
  if (typeof maybeStatus === 'number') payload.status = maybeStatus;
  return new Error(`${VENERA_HOST_ERROR_PREFIX}${JSON.stringify(payload)}`);
}

function installHostApis(bridge: BridgeContext, options: BridgeOptions): void {
  installConstructors(bridge);
  installConvert(bridge);
  installStorage(bridge, options);
  installNetwork(bridge, options.proxyFetch ?? defaultProxyFetch);
  installMisc(bridge, options);
}

function installConstructors(bridge: BridgeContext): void {
  const constructors = bridge.vm.evalCode(`
    globalThis.ComicSource = class ComicSource {
      constructor(value) {
        if (value && typeof value === 'object') Object.assign(this, value);
      }
      loadData(key) { return globalThis.loadData(key); }
      saveData(key, value) { return globalThis.saveData(key, value); }
      deleteData(key) { return globalThis.deleteData(key); }
      loadSetting(key) { return globalThis.loadSetting(key); }
      get isLogged() { return globalThis.isLogged(); }
      translate(key) { return globalThis.translate(key); }
    };
    globalThis.__bunkoMapToObject = function __bunkoMapToObject(value) {
      if (
        !value ||
        typeof value !== 'object' ||
        typeof value.entries !== 'function' ||
        typeof value.size !== 'number'
      ) {
        return value;
      }
      const out = {};
      for (const [key, nested] of value.entries()) out[String(key)] = nested;
      return out;
    };
    globalThis.Comic = function Comic(value) { return value || {}; };
    globalThis.ComicDetails = function ComicDetails(value) {
      if (value && typeof value === 'object' && value.chapters) {
        value.chapters = globalThis.__bunkoMapToObject(value.chapters);
      }
      return value || {};
    };
    globalThis.Comment = function Comment(value) { return value || {}; };
    globalThis.__bunkoNormalize = function __bunkoNormalize(value) {
      if (
        value instanceof Map ||
        (value && typeof value === 'object' && typeof value.entries === 'function' && typeof value.size === 'number')
      ) {
        const out = {};
        for (const [key, nested] of value.entries()) {
          out[String(key)] = __bunkoNormalize(nested);
        }
        return out;
      }
      if (Array.isArray(value)) {
        return value.map((item) => __bunkoNormalize(item));
      }
      if (value && typeof value === 'object') {
        const out = {};
        for (const key of Object.keys(value)) {
          out[key] = __bunkoNormalize(value[key]);
        }
        return out;
      }
      return value;
    };
  `);
  bridge.vm.unwrapResult(constructors).dispose();
}

function installConvert(bridge: BridgeContext): void {
  const convert = bridge.vm.newObject();
  const encodeUtf8Handle = syncHostFunction(bridge, 'Convert.encodeUtf8', (input) =>
    Array.from(new TextEncoder().encode(String(input))),
  );
  const decodeUtf8Handle = syncHostFunction(bridge, 'Convert.decodeUtf8', (input) => {
    if (!Array.isArray(input)) return '';
    return new TextDecoder().decode(Uint8Array.from(input.map((byte) => Number(byte))));
  });
  const encodeBase64Handle = syncHostFunction(bridge, 'Convert.encodeBase64', (input) => {
    if (Array.isArray(input)) {
      return encodeBase64(new TextDecoder().decode(Uint8Array.from(input.map((byte) => Number(byte)))));
    }
    return encodeBase64(String(input));
  });
  const decodeBase64Handle = syncHostFunction(bridge, 'Convert.decodeBase64', (input) =>
    Array.from(decodeBase64(String(input)), (char) => char.charCodeAt(0) & 0xff),
  );
  const md5Handle = syncHostFunction(bridge, 'Convert.md5', (input) => {
    const digest = md5(stringFromVeneraBytes(input));
    return Array.from({ length: digest.length / 2 }, (_, index) =>
      Number.parseInt(digest.slice(index * 2, index * 2 + 2), 16),
    );
  });
  const sha256Handle = syncHostFunction(bridge, 'Convert.sha256', (input) => sha256Hex(String(input)));
  const hmacHandle = syncHostFunction(bridge, 'Convert.hmacString', (first, second, third) => {
    if (typeof third === 'string' && third.toLowerCase() === 'sha256') {
      return hmacString('sha256', stringFromVeneraBytes(first), stringFromVeneraBytes(second));
    }
    return hmacString(String(first), stringFromVeneraBytes(second), stringFromVeneraBytes(third));
  });
  const hexHandle = syncHostFunction(bridge, 'Convert.hexEncode', (input) => {
    if (typeof input === 'string') return hexEncode(input);
    if (Array.isArray(input)) return hexEncode(input.map((byte) => Number(byte)));
    return '';
  });
  const decryptAesEcbHandle = syncHostFunction(bridge, 'Convert.decryptAesEcb', (input, key) =>
    Array.from(decryptAesEcb(normalizeVeneraBytes(input), normalizeVeneraBytes(key))),
  );

  setObjectProp(bridge, convert, 'encodeUtf8', encodeUtf8Handle.dup());
  setObjectProp(bridge, convert, 'decodeUtf8', decodeUtf8Handle.dup());
  setObjectProp(bridge, convert, 'encodeBase64', encodeBase64Handle.dup());
  setObjectProp(bridge, convert, 'decodeBase64', decodeBase64Handle.dup());
  setObjectProp(bridge, convert, 'md5', md5Handle.dup());
  setObjectProp(bridge, convert, 'sha256', sha256Handle.dup());
  setObjectProp(bridge, convert, 'hmacString', hmacHandle.dup());
  setObjectProp(bridge, convert, 'hexEncode', hexHandle.dup());
  setObjectProp(bridge, convert, 'decryptAesEcb', decryptAesEcbHandle.dup());
  setGlobal(bridge, 'Convert', convert);

  setGlobal(bridge, 'encodeUtf8', encodeUtf8Handle);
  setGlobal(bridge, 'decodeUtf8', decodeUtf8Handle);
  setGlobal(bridge, 'encodeBase64', encodeBase64Handle);
  setGlobal(bridge, 'decodeBase64', decodeBase64Handle);
  setGlobal(bridge, 'md5', syncHostFunction(bridge, 'md5', (input) => md5(String(input))));
  setGlobal(bridge, 'sha256', sha256Handle);
  setGlobal(bridge, 'hmacString', hmacHandle);
  setGlobal(bridge, 'hexEncode', hexHandle);
  setGlobal(bridge, 'decryptAesEcb', decryptAesEcbHandle);
}

function stringFromVeneraBytes(value: unknown): string {
  if (Array.isArray(value)) {
    return new TextDecoder().decode(Uint8Array.from(value.map((byte) => Number(byte))));
  }
  return String(value);
}

function normalizeVeneraBytes(value: unknown): Uint8Array | string {
  if (Array.isArray(value)) return Uint8Array.from(value.map((byte) => Number(byte)));
  return String(value);
}

function installStorage(bridge: BridgeContext, options: BridgeOptions): void {
  setGlobal(
    bridge,
    'loadData',
    syncHostFunction(bridge, 'loadData', (key) => options.storage.load(bridge.hostState.auth, String(key)) ?? null),
  );
  setGlobal(
    bridge,
    'saveData',
    syncHostFunction(bridge, 'saveData', (key, value) => {
      mergeHostAuth(bridge, options.storage.save(bridge.hostState.auth, String(key), value));
      return null;
    }),
  );
  setGlobal(
    bridge,
    'deleteData',
    syncHostFunction(bridge, 'deleteData', (key) => {
      mergeHostAuth(bridge, options.storage.delete(bridge.hostState.auth, String(key)));
      return null;
    }),
  );
  setGlobal(
    bridge,
    'loadSetting',
    syncHostFunction(bridge, 'loadSetting', (key) => {
      const stringKey = String(key);
      return options.settings?.[stringKey] ?? options.defaults?.[stringKey] ?? null;
    }),
  );
  setGlobal(
    bridge,
    'isLogged',
    syncHostFunction(bridge, 'isLogged', () => Object.keys(options.storage.snapshot(bridge.hostState.auth)).length > 0),
  );
  setGlobal(bridge, 'translate', syncHostFunction(bridge, 'translate', (value) => value));
}

function mergeHostAuth(bridge: BridgeContext, nextAuth: SourceAuth): void {
  if (!bridge.hostState.auth) {
    bridge.hostState.auth = nextAuth;
    return;
  }
  bridge.hostState.auth.extra = nextAuth.extra;
  if (nextAuth.cookies !== undefined) bridge.hostState.auth.cookies = nextAuth.cookies;
  else delete bridge.hostState.auth.cookies;
  if (nextAuth.anonymous !== undefined) bridge.hostState.auth.anonymous = nextAuth.anonymous;
  else delete bridge.hostState.auth.anonymous;
}

function installNetwork(
  bridge: BridgeContext,
  proxyFetch: (url: string, opts?: ProxyFetchOpts) => Promise<Response>,
): void {
  const network = bridge.vm.newObject();
  setObjectProp(bridge, network, 'get', hostFunction(bridge, 'Network.get', async (url, opts) => {
    const request = requestOptions(opts);
    const response = await proxyFetch(withQuery(String(url), request.query), {
      method: 'GET',
      headers: request.headers,
    });
    return networkResponse(response);
  }));
  setObjectProp(bridge, network, 'post', hostFunction(bridge, 'Network.post', async (url, opts, body) => {
    const request = requestOptions(opts, body);
    const response = await proxyFetch(withQuery(String(url), request.query), {
      method: 'POST',
      headers: request.headers,
      body: request.body,
    });
    return networkResponse(response);
  }));
  setGlobal(bridge, 'Network', network);
  setGlobal(bridge, '__bunkoFetchRaw', hostFunction(bridge, '__bunkoFetchRaw', async (url, init) => {
    const request = requestOptions(init);
    const response = await proxyFetch(String(url), {
      method: request.method,
      headers: request.headers,
      body: request.body,
    });
    return networkResponse(response);
  }));
  const fetchEval = bridge.vm.evalCode(`
    globalThis.fetch = async function bunkoFetch(url, init) {
      const response = await globalThis.__bunkoFetchRaw(url, init || {});
      return {
        ok: response.ok,
        status: response.status,
        headers: response.headers,
        text: async () => response.body,
        json: async () => response.data
      };
    };
  `);
  bridge.vm.unwrapResult(fetchEval).dispose();
}

function installMisc(bridge: BridgeContext, options: BridgeOptions): void {
  setGlobal(
    bridge,
    'createUuid',
    syncHostFunction(bridge, 'createUuid', () => crypto.randomUUID()),
  );
  setGlobal(
    bridge,
    'randomInt',
    syncHostFunction(bridge, 'randomInt', (minOrMax, maybeMax) => {
      if (maybeMax === undefined || maybeMax === null) {
        return Math.floor(Math.random() * Number(minOrMax));
      }
      const min = Math.ceil(Number(minOrMax));
      const max = Math.floor(Number(maybeMax));
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }),
  );
  setGlobal(bridge, 'setTimeout', bridge.vm.newFunction('setTimeout', (callback, delay, ...args) => {
    const callbackHandle = callback.dup();
    const argValues = args.map((arg) => bridge.fromHandle(arg));
    const timer = globalThis.setTimeout(() => {
      bridge.timers.delete(timerId);
      const argHandles = argValues.map((arg) => bridge.toHandle(arg));
      const result = bridge.vm.callFunction(callbackHandle, bridge.vm.undefined, argHandles);
      for (const argHandle of argHandles) argHandle.dispose();
      if (result.error) result.error.dispose();
      else result.value.dispose();
      callbackHandle.dispose();
      while (bridge.runtime.hasPendingJob()) bridge.runtime.executePendingJobs().dispose();
    }, Number(bridge.fromHandle(delay)) || 0);
    const timerId = Number(timer);
    bridge.timers.set(timerId, callbackHandle);
    return bridge.toHandle(timerId);
  }));
  setGlobal(bridge, 'clearTimeout', syncHostFunction(bridge, 'clearTimeout', (timerId) => {
    const numericId = Number(timerId);
    globalThis.clearTimeout(numericId);
    const callback = bridge.timers.get(numericId);
    callback?.dispose();
    bridge.timers.delete(numericId);
    return null;
  }));
  const app = bridge.vm.newObject();
  setObjectProp(bridge, app, 'version', bridge.toHandle('1.6.0'));
  setGlobal(bridge, 'APP', app);

  const ui = bridge.vm.newObject();
  setObjectProp(bridge, ui, 'showMessage', syncHostFunction(bridge, 'UI.showMessage', () => null));
  setGlobal(bridge, 'UI', ui);

  const htmlDocument = bridge.vm.newObject();
  setObjectProp(
    bridge,
    htmlDocument,
    'parse',
    syncHostFunction(bridge, 'HtmlDocument.parse', () => {
      throw new UnsupportedVeneraApiError('HtmlDocument');
    }),
  );
  setGlobal(bridge, 'HtmlDocument', htmlDocument);

  const image = bridge.vm.newObject();
  setObjectProp(
    bridge,
    image,
    'modifyImage',
    syncHostFunction(bridge, 'Image.modifyImage', () => {
      throw new UnsupportedVeneraApiError('Image.modifyImage');
    }),
  );
  setGlobal(bridge, 'Image', image);

  setGlobal(bridge, 'sendMessage', syncHostFunction(bridge, 'sendMessage', () => null));
  setGlobal(bridge, '__bunkoSourceId', bridge.toHandle(options.sourceId));
  const consoleObject = bridge.vm.newObject();
  for (const method of ['debug', 'log', 'info', 'warn', 'error']) {
    setObjectProp(bridge, consoleObject, method, syncHostFunction(bridge, `console.${method}`, () => null));
  }
  setGlobal(bridge, 'console', consoleObject);
}

function requestOptions(value: unknown, bodyOverride?: unknown): {
  method?: string;
  headers?: HeadersInit;
  query?: Record<string, string>;
  body?: BodyInit;
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { body: bodyInit(bodyOverride) };
  }
  const record = value as Record<string, unknown>;
  const looksLikeRequestInit =
    'headers' in record || 'query' in record || 'body' in record || 'method' in record;
  const headers = looksLikeRequestInit ? headerInit(record.headers) : headerInit(record);
  const query = looksLikeRequestInit ? queryInit(record.query) : undefined;
  const body = bodyInit(bodyOverride ?? (looksLikeRequestInit ? record.body : undefined));
  const method = typeof record.method === 'string' ? record.method : undefined;
  return { method, headers, query, body };
}

function headerInit(value: unknown): HeadersInit | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const directHeaders: Record<string, string> = {};
  const forwardedHeaders: Record<string, string> = {};
  for (const [key, headerValue] of Object.entries(value as Record<string, unknown>)) {
    if (!isForwardableHeader(key)) continue;
    const normalized = key.toLowerCase();
    const stringValue = String(headerValue);
    if (['accept', 'content-type', 'range', 'x-csrftoken'].includes(normalized)) {
      directHeaders[key] = stringValue;
    }
    forwardedHeaders[key] = stringValue;
  }
  if (Object.keys(forwardedHeaders).length > 0) {
    directHeaders[FORWARDED_HEADERS_HEADER] = encodeForwardedHeaders(forwardedHeaders);
  }
  return Object.keys(directHeaders).length > 0 ? directHeaders : undefined;
}

function isForwardableHeader(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    lower !== 'cookie' &&
    lower !== 'set-cookie' &&
    lower !== 'set-cookie2' &&
    lower !== 'host' &&
    lower !== 'content-length' &&
    lower !== 'connection' &&
    lower !== 'transfer-encoding'
  );
}

function encodeForwardedHeaders(headers: Record<string, string>): string {
  return btoa(JSON.stringify(headers));
}

function queryInit(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const query: Record<string, string> = {};
  for (const [key, queryValue] of Object.entries(value as Record<string, unknown>)) {
    query[key] = String(queryValue);
  }
  return query;
}

function bodyInit(value: unknown): BodyInit | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function withQuery(url: string, query: Record<string, string> | undefined): string {
  if (!query) return url;
  const parsed = new URL(url);
  for (const [key, value] of Object.entries(query)) parsed.searchParams.set(key, value);
  return parsed.toString();
}

async function networkResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  let data: unknown = text;
  if (text.trim()) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  return {
    ok: response.ok,
    status: response.status,
    data,
    body: text,
    headers: Object.fromEntries(response.headers.entries()),
  };
}

function normalizePlugin(value: unknown): VeneraRuntimePlugin {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Venera plugin did not evaluate to a ComicSource object.');
  }
  return value as VeneraRuntimePlugin;
}
